"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useQueryState } from "nuqs";

import { GetLoginFlowResponse } from "@shared/proto/tenki/cloud/identity/v1beta1/identity_service_pb";
import { Button } from "@shared/ui/button";

import { api } from "~/trpc/client";
import LoginForm from "./form";

interface LoginContainerProps {
  flowId: string;
  data: GetLoginFlowResponse;
}

export default function LoginContainer(params: LoginContainerProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [flowId, setFlowId] = useQueryState("flow", { defaultValue: params.flowId });
  const returnTo = searchParams.get("return_to");

  const ctx = api.useUtils();
  const $getFlow = api.identity.public.getFlow.useQuery(
    { flowId, type: "login" },
    { initialData: params.data, refetchOnWindowFocus: false },
  );
  const $createFlow = api.identity.public.createFlow.useMutation({
    onSuccess: (data) => {
      if (!data) return;
      setFlowId(data.flowId);
      ctx.identity.public.getFlow.invalidate({ flowId: data.flowId, type: "login" });
    },
    trpc: { context: { skipBatch: true } },
  });

  const { flow, errors = [] } = $getFlow.data as GetLoginFlowResponse;

  function onSignInPage() {
    router.replace("/api/auth/logout");
  }

  function onSessionExpired() {
    $createFlow.mutate({ flowType: "login", returnTo: returnTo ?? undefined });
  }

  return (
    <>
      <LoginForm
        flowId={flowId}
        flowError={errors.length ? errors[0] : undefined}
        csrfToken={flow?.csrfToken}
        loginMethod={flow?.loginMethod}
        onSessionExpired={onSessionExpired}
      />
      {flow?.loginMethod === "totp" ? (
        <div className="mt-4 text-center text-xs">
          <Button variant="link" onClick={onSignInPage}>
            Go back to Sign In Page
          </Button>
        </div>
      ) : (
        <div className="text-static-primary mt-6 text-center">
          <span>Don&apos;t have an account?</span>{" "}
          <Link
            href="/auth/registration"
            prefetch={false}
            className="text-cta-link-rest hover:text-cta-link-hovered font-medium underline decoration-solid underline-offset-[3px]"
          >
            Sign up
          </Link>
        </div>
      )}
    </>
  );
}
